import { useEffect, useState, useCallback } from 'react';
import useSWRMutation from 'swr/mutation';
import { NudosButton, NudosSearchBar, NudosStaticAlertInComponent } from 'components/NudosComponents';
import { displaySuccessNotification } from 'utils/displayNudosStandardNotifications';
import { ICreateReservationStockItem } from 'types/stockOrders';
import { createReservationStock } from 'services/stockOrders';
import { SWR_KEYS } from 'constants/constants';
import useStockReservationState from 'state/useStockReservationState';
import { IFloatSelectSerialItem } from '../../FloatSelectSerial';
import BulkSerialItem from './BulkSerialItem/BulkSerialItem';

import './BulkSelectSerial.scss';

interface IBulkSelectSerial {
  items: IFloatSelectSerialItem[];
  onSuccess: () => void;
  requiredUnits: number;
}

/**
 * Componente para la selección masiva de seriales de stock
 * @param items - Lista de items disponibles para reservar
 * @param onSuccess - Callback ejecutado al completar la reserva exitosamente
 * @param requiredUnits - Número de unidades requeridas para la reserva
 *
 * El componente incluye un buscador con dos comportamientos:
 * 1. Al buscar un serial (combinación de letras y números sin espacios), realiza una búsqueda exacta
 * 2. Al buscar por otros campos (proveedor o factura), filtra por coincidencia parcial de todas las palabras
 *    ingresadas en al menos uno de los campos.
 */
const BulkSelectSerial = ({ items, onSuccess, requiredUnits }: IBulkSelectSerial) => {
  const { setSelectedItems, setRefreshStockProductAvailabilityData } = useStockReservationState();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSerials, setSelectedSerials] = useState<IFloatSelectSerialItem[]>([]);
  const [unselectedSerials, setUnselectedSerials] = useState<IFloatSelectSerialItem[]>([]);
  const [savedSaleIds, setSavedSaleIds] = useState<(number | string)[]>([]);

  const disabledStyles = selectedSerials?.length >= requiredUnits

  const { trigger: createReservationStockTrigger, isMutating: isLoadingCreateReservationStock } = useSWRMutation(
    SWR_KEYS.createReservationStock,
    async (
      __,
      {
        arg
      }: {
        arg: ICreateReservationStockItem[];
      }
    ) => {
      return await createReservationStock(arg);
    },
    {
      onSuccess: () => {
        displaySuccessNotification({
          customJSXMessage: <>Reserva de stock creada correctamente</>,
          duration: 3000
        });
        onSuccess();
        setRefreshStockProductAvailabilityData(true);
      }
    }
  );

  const updateGlobalSelectedItems = useCallback(
    (serials: IFloatSelectSerialItem[]) => {
      setSelectedItems(
        serials.map(item => ({
          stockProductId: item.stockProductId,
          saleId: item?.saleId ?? 0
        }))
      );
    },
    [setSelectedItems]
  );

  const sortByDate = (items: IFloatSelectSerialItem[]) => {
    return [...items].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  };

  const sortByFirstOut = (items: IFloatSelectSerialItem[]) => {
    return items.slice().sort((a, b) => {
      if (a.isFirtsOut && !b.isFirtsOut) return -1;
      if (!a.isFirtsOut && b.isFirtsOut) return 1;
      return 0;
    });
  };

  const markFirstOutItems = (items: IFloatSelectSerialItem[]) => {
    return items.map(item => ({
      ...item,
      isFirtsOut: item.isFirtsOut
    }));
  };

  const handleInitialSelection = () => {
    const sortedItems = sortByDate(items);
    
    const markedItems = sortedItems.map((item, index) => ({
      ...item,
      isFirtsOut: index < requiredUnits
    }));

    const initialSelected = markedItems.slice(0, requiredUnits);
    const initialUnselected = markedItems.slice(requiredUnits);

    setSelectedSerials(initialSelected);
    setUnselectedSerials(initialUnselected);
    updateGlobalSelectedItems(initialSelected);
  };

  const filterSerialItems = (items: IFloatSelectSerialItem[]) => {
    const trimmedSearch = searchTerm.trim().toLowerCase();
    if (!trimmedSearch) return items;

    return items.filter(item => {
      // Para búsqueda de seriales (cuando el término buscado es un serial)
      if (trimmedSearch === item.serial.toLowerCase()) {
        return true;
      }

      // Para búsqueda por proveedor o factura (cuando no hay coincidencia exacta con serial)
      const searchTerms = trimmedSearch
        .toLowerCase()
        .split(/\s+/)
        .filter(term => term.length > 0);
      const itemFields = [item.billingNumber.toLowerCase(), item.providerName.toLowerCase()];

      return searchTerms.every(term => itemFields.some(field => field.includes(term)));
    });
  };

  const filteredSelectedSerials = filterSerialItems(selectedSerials);
  const filteredUnselectedSerials = filterSerialItems(unselectedSerials);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };

  const handleCreateReservationStock = () => {
    const reservationItems = selectedSerials?.map(item => {
      return {
        stockProductId: item.stockProductId,
        saleId: item.saleId ?? 0
      };
    });
    createReservationStockTrigger(reservationItems);
  };

  const toggleSerialSelection = (serial: IFloatSelectSerialItem, isSelected: boolean) => {
    if (isSelected) {
      if (serial.saleId) {
        setSavedSaleIds(prev => [...prev, serial.saleId!]);
      }

      const newSelectedSerials = selectedSerials?.filter(item => item.stockProductId !== serial.stockProductId);
      const sortedSelected = sortByDate(newSelectedSerials);
      const markedSelected = markFirstOutItems(sortedSelected);

      setSelectedSerials(markedSelected);
      setUnselectedSerials(prev => {
        const newUnselected = [...prev, serial];
        return sortByDate(newUnselected);
      });
      updateGlobalSelectedItems(markedSelected);
    } else {
      if (selectedSerials.length < requiredUnits) {
        const serialWithSaleId = {
          ...serial,
          saleId: savedSaleIds.length > 0 ? savedSaleIds[0] : serial.saleId
        };

        if (savedSaleIds.length > 0) {
          setSavedSaleIds(prev => prev.slice(1));
        }

        const newSelectedSerials = [...selectedSerials, serialWithSaleId];
        const sortedSelected = sortByDate(newSelectedSerials);
        const markedSelected = markFirstOutItems(sortedSelected);

        setSelectedSerials(markedSelected);
        setUnselectedSerials(prev => {
          const newUnselected = prev.filter(item => item.stockProductId !== serial.stockProductId);
          return sortByDate(newUnselected);
        });
        updateGlobalSelectedItems(markedSelected);
      }
    }
  };

  useEffect(() => {
    handleInitialSelection();
  }, [requiredUnits, updateGlobalSelectedItems]);

  return (
    <div className="bulkSelectSerial">
      <div className="subTitle">Podrás reservar todas las unidades disponible que se encuentre en el stock propio</div>
      <NudosStaticAlertInComponent
        customClassname="customNudosStaticAlertInComponent"
        type="warning"
        alertOptions={{
          title: (
            <>
              Se seleccionaron automáticamente las unidades con salida más próxima. Puedes cambiarlas si prefieres
              reservar otras.
            </>
          )
        }}
        customTitleClass="customTitleAlert"
        hideTitleClass
      />
      <div className="searchContainer">
        <NudosSearchBar
          handleChange={handleSearch}
          placeholder="Buscar por serial, n° de factura o proveedor"
          hasDeleteButton
          includeMagnifierIcon
        />
      </div>

      <div className="serialsContainer">
        {!!filteredSelectedSerials?.length && (
          <div className="selectedSerialsSection">
            <div className="sectionTitle">Unidades Seleccionadas</div>
            <div className="serialsList">
              {filteredSelectedSerials?.map((item, index) => (
                <BulkSerialItem
                  key={`selected-${item.stockProductId}-${index}`}
                  index={index}
                  item={item}
                  isSelected
                  toggleSerialSelection={toggleSerialSelection}
                  selectedSerials={selectedSerials}
                  isUnselectedSerialsList={false}
                  requiredUnits={requiredUnits}
                />
              ))}
            </div>
          </div>
        )}

     {!!filteredSelectedSerials?.length && !!filteredUnselectedSerials?.length && <div className="divider" />}

        {!!filteredUnselectedSerials?.length && (
          <div className="unselectedSerialsSection">
            <div className="sectionTitle">Otras Unidades</div>
            <div className={`serialsList ${disabledStyles ? 'opacity-50 cursor-not-allowed' : ''}`}>
              {sortByFirstOut(filteredUnselectedSerials).map((item, index) => (
                <BulkSerialItem
                  key={`unselected-${item.stockProductId}-${index}`}
                  index={index}
                  item={item}
                  isSelected={false}
                  toggleSerialSelection={toggleSerialSelection}
                  selectedSerials={selectedSerials}
                  unselectedSerialsList={unselectedSerials}
                  isUnselectedSerialsList
                  requiredUnits={requiredUnits}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="buttonContainer">
        <div className="confirmationText">¿Seguro que deseas continuar?</div>
        <NudosButton
          isButtonLoading={isLoadingCreateReservationStock}
          isButtonDisabled={isLoadingCreateReservationStock || !selectedSerials?.length}
          buttonText={isLoadingCreateReservationStock ? 'Reservando unidades...' : 'Si, Confirmar reserva de unidades'}
          customWidth="208px"
          customHeight="38px"
          handleClick={handleCreateReservationStock}
        />
      </div>
    </div>
  );
};

export default BulkSelectSerial;
